/**
 * Pure display helpers for travel segments, shared by the itinerary day pane's
 * travel row, the public board's `TravelSegment` row and the trip-overview
 * itinerary tile's compact segment row — one formatting source so
 * "12 Minutes · 0.4 Miles" reads identically on all three.
 * Structural param types keep the overview core free of api-types coupling.
 */

import { pluralize } from '@/utils/plural'

/** The route fields the display needs (the walking, transit or driving route). */
export interface SegmentRouteDisplay {
	durationSeconds: number | null
	distanceMeters: number | null
	transportMode: string
}

/** Minimum segment shape: the cached routes a segment may carry. */
export interface SegmentRoutesInput {
	walkingRoute?: SegmentRouteDisplay | null
	transitRoute?: SegmentRouteDisplay | null
	/** Only present where neither transit nor walking has a route — e.g. a hop in Japan, which Google Routes has no transit data for. */
	drivingRoute?: SegmentRouteDisplay | null
}

export const formatSegmentDuration = (seconds: number | null): string => {
	if (seconds === null) return 'N/A'
	const minutes = Math.round(seconds / 60)
	if (minutes <= 90) return `${minutes} ${pluralize(minutes, 'Minute')}`
	const hours = Math.floor(minutes / 60)
	const remMinutes = minutes % 60
	const hourLabel = pluralize(hours, 'Hour')
	if (remMinutes === 0) return `${hours} ${hourLabel}`
	return `${hours} ${hourLabel} ${remMinutes} ${pluralize(remMinutes, 'Minute')}`
}

export const formatSegmentDistance = (meters: number | null): string => {
	if (meters === null) return 'N/A'
	const miles = meters / 1609.344
	// Read to one decimal, so only an exact "1.0" is singular.
	const shown = miles.toFixed(1)
	return `${shown} ${shown === '1.0' ? 'Mile' : 'Miles'}`
}

const hasRouteData = (route: SegmentRouteDisplay | null | undefined): boolean =>
	route != null && (route.durationSeconds !== null || route.distanceMeters !== null)

/**
 * The route a segment should display: transit when it has data, else walking,
 * else the backend's driving fallback, else whichever exists (so a data-less
 * segment still shows "ETA N/A"). The picked route carries its own
 * `transportMode`, so the icon, the ETA and the Maps link always agree.
 */
export const pickSegmentRoute = <T extends SegmentRoutesInput>(
	segment: T,
): NonNullable<T['transitRoute'] | T['walkingRoute'] | T['drivingRoute']> | null => {
	if (hasRouteData(segment.transitRoute)) return segment.transitRoute!
	if (hasRouteData(segment.walkingRoute)) return segment.walkingRoute!
	if (hasRouteData(segment.drivingRoute)) return segment.drivingRoute!
	return segment.transitRoute ?? segment.walkingRoute ?? segment.drivingRoute ?? null
}

/** One-line summary, e.g. "12 Minutes · 0.4 Miles", or "ETA N/A" without route data. */
export const segmentRouteLabel = (route: SegmentRouteDisplay): string => {
	if (route.durationSeconds === null && route.distanceMeters === null) return 'ETA N/A'
	return `${formatSegmentDuration(route.durationSeconds)} · ${formatSegmentDistance(route.distanceMeters)}`
}

/** The Google Maps directions link between a route's two places, in the route's own travel mode. */
export const directionsUrl = (route: {
	originPlaceId: string
	destinationPlaceId: string
	transportMode: string
}): string =>
	`https://www.google.com/maps/dir/?api=1&origin=${route.originPlaceId}&origin_place_id=${route.originPlaceId}&destination=${route.destinationPlaceId}&destination_place_id=${route.destinationPlaceId}&travelmode=${route.transportMode.toLowerCase()}`
